/**
 * Suchfeld in der Kopfzeile (UC-017 Schritt 1, docs/ux-design.md 5.5): Die Suche startet nach
 * Eingabe von 2 Zeichen und 300 ms Pause. Auf der Seite der Volltextsuche ersetzt sie nur die
 * Trefferliste, damit die Weltkarte stehen bleibt; auf den anderen Seiten öffnet sie die
 * Volltextsuche. Auf dem Mobiltelefon öffnet das Such-Symbol das Feld über die ganze Kopfzeile.
 */
export const PAUSE_MS = 300;
export const MINDESTLAENGE = 2;

const OFFEN = 'suche--offen';
/** Merkt sich über das Öffnen der Volltextsuche hinweg, dass der Besucher noch tippt */
const FOKUS_MERKEN = 'travelmap.suche.fokus';

export interface Seite {
  /** Öffnet die Seite der Volltextsuche */
  oeffnen(adresse: string): void;
  /** Nur auf der Seite der Volltextsuche: ersetzt die Trefferliste durch die der Adresse */
  ersetzen?: (adresse: string) => Promise<void>;
  /** Ablage für den Fokus über das Öffnen hinweg; fehlt sie, geht nur der Fokus verloren */
  ablage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null;
}

export function sucheEinrichten(formular: HTMLFormElement, seite: Seite): void {
  const feld = formular.querySelector<HTMLInputElement>('input[name="q"]');
  const knopf = formular.querySelector<HTMLButtonElement>('button');
  if (!feld || !knopf) {
    return;
  }
  const basis = formular.getAttribute('action') ?? '/suche';
  const adresse = (begriff: string): string => (begriff === '' ? basis : `${basis}?q=${encodeURIComponent(begriff)}`);
  let zeitgeber: ReturnType<typeof setTimeout> | undefined;
  let gesucht = feld.value.trim();

  const suchen = (sofort: boolean): void => {
    const begriff = feld.value.trim();
    if (seite.ersetzen) {
      // Auf der Seite der Volltextsuche meldet das System auch A1
      if (sofort || begriff !== gesucht) {
        gesucht = begriff;
        void seite.ersetzen(adresse(begriff));
      }
    } else if (sofort || [...begriff].length >= MINDESTLAENGE) {
      merken(seite.ablage, () => seite.ablage?.setItem(FOKUS_MERKEN, '1'));
      seite.oeffnen(adresse(begriff));
    }
  };

  feld.addEventListener('input', () => {
    clearTimeout(zeitgeber);
    zeitgeber = setTimeout(() => suchen(false), PAUSE_MS);
  });
  formular.addEventListener('submit', (ereignis) => {
    clearTimeout(zeitgeber);
    if (seite.ersetzen) {
      ereignis.preventDefault();
      suchen(true);
    }
  });

  // Mobiltelefon: Solange das Feld verborgen ist, öffnet das Such-Symbol es, statt zu suchen
  knopf.addEventListener('click', (ereignis) => {
    if (getComputedStyle(feld).display === 'none') {
      ereignis.preventDefault();
      formular.classList.add(OFFEN);
      feld.focus();
    }
  });
  if (!seite.ersetzen) {
    const schliessen = (): void => {
      if (feld.value.trim() === '') {
        formular.classList.remove(OFFEN);
      }
    };
    feld.addEventListener('keydown', (ereignis) => {
      if (ereignis.key === 'Escape') {
        feld.value = '';
        schliessen();
        knopf.focus();
      }
    });
    feld.addEventListener('blur', schliessen);
  }

  // Wer auf einer anderen Seite zu tippen begann, tippt auf der Seite der Volltextsuche weiter
  if (seite.ersetzen && merken(seite.ablage, () => seite.ablage?.getItem(FOKUS_MERKEN)) === '1') {
    merken(seite.ablage, () => seite.ablage?.removeItem(FOKUS_MERKEN));
    feld.focus();
    feld.setSelectionRange(feld.value.length, feld.value.length);
  }
}

/** Die Ablage kann fehlen oder den Zugriff verweigern, z. B. im privaten Fenster */
function merken<T>(ablage: Seite['ablage'], zugriff: () => T): T | undefined {
  if (!ablage) {
    return undefined;
  }
  try {
    return zugriff();
  } catch {
    return undefined;
  }
}

/**
 * Ersetzt das Suchergebnis durch das der Adresse, passt Titel und Adresse an und meldet
 * «suchergebnis», damit die Weltkarte die neuen Treffer hervorhebt. Eine überholte Antwort wird
 * verworfen; schlägt das Laden fehl, öffnet die Adresse als neue Seite.
 */
export function ergebnisErsetzen(dokument: Document, laden: (adresse: string) => Promise<string>,
  oeffnen: (adresse: string) => void): (adresse: string) => Promise<void> {
  let zaehler = 0;
  return async (adresse: string): Promise<void> => {
    const anfrage = ++zaehler;
    let html: string;
    try {
      html = await laden(adresse);
    } catch {
      oeffnen(adresse);
      return;
    }
    if (anfrage !== zaehler) {
      return;
    }
    const neu = new DOMParser().parseFromString(html, 'text/html');
    const ergebnis = dokument.querySelector('#suchergebnis');
    const neuesErgebnis = neu.querySelector('#suchergebnis');
    if (!ergebnis || !neuesErgebnis) {
      oeffnen(adresse);
      return;
    }
    ergebnis.replaceChildren(...Array.from(neuesErgebnis.childNodes, (knoten) => dokument.importNode(knoten, true)));
    dokument.title = neu.title;
    dokument.defaultView?.history.replaceState(null, '', adresse);
    dokument.dispatchEvent(new CustomEvent('suchergebnis'));
  };
}

/**
 * Lädt die Treffer nach. Der Header unterscheidet das Nachladen vom Laden der Suchseite, damit eine
 * Suche in der Statistik nur einmal zählt (UC-024 GR-001, ADR-008).
 */
export async function htmlLaden(adresse: string): Promise<string> {
  const antwort = await fetch(adresse, { headers: { Accept: 'text/html', 'X-Travelmap-Nachladen': '1' } });
  if (!antwort.ok) {
    throw new Error(`HTTP ${antwort.status}`);
  }
  return antwort.text();
}
